import React, { memo } from 'react';
import { Sprout, PenLine, Mic } from 'lucide-react';

/**
 * JournalRhythmCard — the static, always-on top of Home: a grounded
 * one-line encouragement, the Mon–Sun rhythm grid, and the core threads
 * (values) the user keeps returning to. Replaces the old auto-dismissing
 * JournalPulseBanner and the redundant "Hi, name" headline.
 *
 * All copy — the tagline, the two section labels — is resolved server-side
 * (see backend/services/copyConfig.js) so wording and thresholds tune via a
 * backend deploy, never a frontend release. This component only lays it out.
 *
 * Pure presentational: no local state, no timers, no portal — a plain
 * memoized render keyed off `pulse`, cheap on low-power mobile/iPad browsers
 * (no blur filters, no JS animation loop, CSS gradients only).
 */

const SAGE = '#7C9A82';
const AMBER = '#D4A574';

function JournalRhythmCard({ pulse }) {
  if (!pulse) return null;
  const values = Array.isArray(pulse.values) ? pulse.values : [];
  const stats = pulse.stats || {};
  const copy = pulse.copy || {};
  const weekActivity = Array.isArray(stats.weekActivity) ? stats.weekActivity : [];
  const hasContent = values.length > 0 || (stats.totalDays || 0) > 0;
  if (!hasContent) return null;

  const sectionLabel = copy.rhythmSectionLabel || "This week's gentle rhythm";
  const threadsLabel = copy.rhythmThreadsLabel || 'Core threads';

  // Days lit in the Mon-Sun grid below — deliberately NOT stats.daysThisWeek
  // (a rolling 7-day window, kept so the tagline tier never snaps to zero on
  // a new week). Showing the rolling count next to a calendar-week grid let
  // the two disagree (e.g. "5x this week" over only 3 lit dots).
  const activeDays = weekActivity.filter((d) => d.active).length;

  return (
    <div
      className="relative rounded-[1.75rem] border px-4 py-6 sm:px-5 sm:py-7"
      style={{
        backgroundColor: '#FFFEFB',
        borderColor: '#EFEAE2',
        backgroundImage: `linear-gradient(135deg, ${SAGE}14 0%, transparent 55%, ${AMBER}0F 100%)`,
        boxShadow: '0 12px 32px -16px rgba(60, 50, 40, 0.18)',
      }}
    >
      {/* One continuous paragraph, not two stacked blocks — wordsSummary
          reads as the tagline's sentence continuing, not a separate
          thought, so it rides the same <p> with a single space between. */}
      {stats.tagline && (
        <p
          className="font-serif text-[18px] leading-snug text-stone-800"
          style={{ letterSpacing: '-0.2px' }}
        >
          {stats.tagline}
          {stats.wordsSummary && ` ${stats.wordsSummary}`}
        </p>
      )}

      <div className="h-px bg-stone-200 my-4" />

      {/* Rhythm grid. The "Nx this week" badge rides this label's row
          (right-aligned, never wraps) instead of sitting alone on its own
          top line — same pattern entriesThisMonth uses on the threads row
          below, so both section headers share one layout idiom. */}
      <div className="flex items-center justify-between">
        <p className="font-serif italic text-[13px] font-semibold text-stone-500" style={{ letterSpacing: '0.2px' }}>
          {sectionLabel}
        </p>
        {activeDays > 0 && (
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1.5 text-[12px] font-bold whitespace-nowrap"
            style={{ backgroundColor: `${SAGE}24`, color: SAGE }}
          >
            {activeDays}x this week
          </span>
        )}
      </div>
      {weekActivity.length > 0 && (
        <div className="mt-3 flex items-center justify-between">
          {weekActivity.map((d) => (
            <div key={d.day} className="flex flex-col items-center gap-2">
              <span className="text-[11px] font-semibold text-stone-400">{d.day}</span>
              <span
                className="h-[30px] w-[30px] rounded-full flex items-center justify-center"
                style={
                  d.active
                    ? {
                        backgroundImage: `linear-gradient(135deg, ${SAGE}, ${SAGE}B8)`,
                        boxShadow: `0 3px 10px -2px ${SAGE}59`,
                      }
                    : { border: '1.4px solid #E7E5E4' }
                }
              >
                {d.active && (
                  d.icon === 'voice'
                    ? <Mic size={14} strokeWidth={2.4} color="#FFFFFF" />
                    : d.icon === 'written'
                      ? <PenLine size={14} strokeWidth={2.4} color="#FFFFFF" />
                      : <Sprout size={14} strokeWidth={2.4} color="#FFFFFF" />
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Core threads — entries-this-month rides this label's row (it has
          room to spare there) instead of sitting alone as a trailing line
          with empty space beside it. */}
      {values.length > 0 && (
        <>
          <div className="mt-5 flex items-center justify-between">
            <p className="font-serif italic text-[13px] font-semibold text-stone-500" style={{ letterSpacing: '0.2px' }}>
              {threadsLabel}
            </p>
            {stats.entriesThisMonth > 0 && (
              <p className="text-[12.5px] font-medium text-stone-400">
                {stats.entriesThisMonth} {stats.entriesThisMonth === 1 ? 'entry' : 'entries'} this month
              </p>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {values.map((v, i) => {
              const tint = i % 2 === 0 ? SAGE : AMBER;
              return (
                <span
                  key={v}
                  className="inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-semibold"
                  style={{ backgroundColor: `${tint}24`, color: tint, border: `1px solid ${tint}48` }}
                >
                  {v}
                </span>
              );
            })}
          </div>
        </>
      )}
      {values.length === 0 && stats.entriesThisMonth > 0 && (
        <p className="mt-5 text-[12.5px] font-medium text-stone-400">
          {stats.entriesThisMonth} {stats.entriesThisMonth === 1 ? 'entry' : 'entries'} this month
        </p>
      )}
    </div>
  );
}

// Memoized: Home re-renders often (chat, journal, profile state all live in
// the same root component), but this card's own props only change when the
// journal-pulse query actually refetches.
export default memo(JournalRhythmCard);
